import ClubInvoice from '../dto/ClubInvoice.js';
import Error from '../model/Error.js';
import Helper from '../accounting/Helper.js';
import OpenItems from '../dto/OpenItems.js';
import axios from 'axios';

export default class ClubInvoiceRepository {
    static getCountByClubIdForDunningLevel1(clubId) {
        const query = `?dunningStatus[]=1`
            + `&invoiceStatus[]=${ClubInvoice.STATUS_OPEN
            }&invoiceStatus[]=${ClubInvoice.STATUS_CHARGEBACK
            }&invoiceStatus[]=${ClubInvoice.STATUS_UNEQUAL}`;
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/clubs/${clubId}/invoices/count${query}`)
                .then((response) => resolve(response.data.data.count))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static getCountAndInvoicesByClubIdAndFilters(clubId, query) {
        return Promise.all([
            this.getCountByClubId(clubId, query),
            this.getInvoicesByClubId(clubId, query),
        ]);
    }

    static getCountByClubId(clubId, query) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/clubs/${clubId}/invoices/count${query}`)
                .then((response) => resolve(response.data.data.count))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static getInvoicesByClubId(clubId, query) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/clubs/${clubId}/invoices${query}`)
                .then((response) => resolve(response.data.data.map((clubFee) => ClubInvoice.fromData(clubFee))))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static getOpenItemsByClubId(clubId, query) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/clubs/${clubId}/invoices/open-items${query}`)
                .then((response) => resolve(OpenItems.fromData(response.data.data)))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static getCountAndInvoicesByMemberIdAndFilters(memberId, query) {
        return Promise.all([
            this.getCountByMemberId(memberId, query),
            this.getInvoicesByMemberId(memberId, query),
        ]);
    }

    static getCountByMemberId(memberId, query) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/invoices/count${query}`)
                .then((response) => resolve(response.data.data.count))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static getInvoicesByMemberId(memberId, query) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/invoices${query}`)
                .then((response) => resolve(response.data.data.map((clubFee) => ClubInvoice.fromData(clubFee))))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static getOpenItemsByMemberId(memberId, query) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/invoices/open-items${query}`)
                .then((response) => resolve(OpenItems.fromData(response.data.data)))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static find(clubInvoiceId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/club-invoices/${clubInvoiceId}`)
                .then((response) => resolve(ClubInvoice.fromData(response.data.data)))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static updateDunningStatus(clubInvoiceId, status) {
        const data = { dunningStatus: status };
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/club-invoices/${clubInvoiceId}/dunning-status`, data)
                .then(() => resolve())
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static bulkIncreaseDunningStatus(clubInvoiceIds) {
        const data = { invoices: clubInvoiceIds };
        return new Promise((resolve, reject) => {
            axios
                .put('/club/club-invoices/dunning-status', data)
                .then(() => resolve())
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static setChargeback(clubInvoiceId, data) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/club-invoices/${clubInvoiceId}/chargeback`, data)
                .then((response) => resolve(response))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static deleteChargeback(clubInvoiceId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/club-invoices/${clubInvoiceId}/chargeback`)
                .then((response) => resolve(response))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static bulkUpdateStatus(clubId, data) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/clubs/${clubId}/invoices`, data)
                .then(() => resolve())
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static deleteByInvoiceId(clubInvoiceId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/club-invoices/${clubInvoiceId}`)
                .then((response) => resolve(response))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static cancelByClubIdAndInvoiceIds(clubId, clubInvoiceIds) {
        const data = { invoices: clubInvoiceIds };
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/clubs/${clubId}/reverse-invoices`, data)
                .then(() => resolve())
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static getXInvoiceByInvoiceId(invoiceId, isReverseInvoice) {
        const url = isReverseInvoice
            ? `/club/club-reverse-invoices/${invoiceId}/x-invoices`
            : `/club/club-invoices/${invoiceId}/x-invoices`;
        return new Promise((resolve, reject) => {
            axios
                .get(url)
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    const filename = new Helper().getFilenameFromHeaders(response.headers);
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    resolve();
                })
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }
}
